<template>
    <div class="search-container" v-click-outside="hideResults" @focusin="openResultsIfAny">
        <ui-input id="searchAccount" :clearOptions="true" name="searchAccount" label="Search accounts" type="text" placeholder="Search account"
            v-model:value="searchTerm" autocomplete="off" @clearSearch="handleSearchCancel"/>
        <ul v-if="isListVisible && (searching || searchResults.length > 0)" class="results-list">
            <template v-if="searching">
                <li v-for="n in 3" :key="`skeleton-${n}`" class="result-item">
                    <div class="result-button">
                        <div class="result-content">
                            <div class="result-main">
                                <span class="skeleton-line skeleton-line--name"></span>
                                <span class="skeleton-line skeleton-line--email"></span>
                            </div>
                        </div>
                    </div>
                </li>
            </template>
            <li v-for="result in searchResults" v-else :key="result.id" class="result-item">
                <button type="button" class="result-button" @click="handleAccountSelection(result)">
                    <div class="result-content">
                        <div class="result-main">
                            <span class="result-name">{{ result.first_name }} {{ result.last_name || '' }}</span>
                            <span class="result-email">{{ result.email }}</span>
                        </div>
                        <div v-if="result.name" class="result-badge" :title="result.name">
                            <span class="result-badge-text">{{ result.name }}</span>
                        </div>
                    </div>
                </button>
            </li>
        </ul>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import UiInput from '@/components/ui/Input';
import { mapActions, mapGetters } from 'vuex';

export default {
    components: { UiInput },
    data() {
        return {
            searchTerm: '',
            searchResults: [],
            abortController: null,
            isListVisible: false,
            isSelecting: true,
            searching: false,
        };
    },
    computed: {
        ...mapGetters({
            account: 'globalFilters/account',
        }),
    },
    methods: {
        ...mapActions({ setAccount: 'globalFilters/setAccount' }),
        resetSearchData() {
            this.debouncedFetchData.cancel();
            this.abortController?.abort();
            this.searchResults = [];
            this.isListVisible = false;
            this.searching = false;
        },
        async fetchData() {
            const trimmed_term = this.searchTerm?.trim();
            
            if (!trimmed_term) {
                this.resetSearchData();
                return;
            }

            this.abortController?.abort();
            this.abortController = new AbortController();

            try {
                const { data } = await this.$axios.get(`/_/accounts/search?q=${encodeURIComponent(trimmed_term)}`, {
                    signal: this.abortController.signal
                });
                if (trimmed_term !== this.searchTerm?.trim()) {
                    return;
                }
                this.searchResults = data;
                this.isListVisible = true;
                this.searching = false;
            } catch (error) {
                if (error.code === 'ERR_CANCELED') {
                    return;
                }
                this.searchResults = [];
                this.searching = false;
            }
        },
        hideResults() {
            this.isListVisible = false;
        },
        openResultsIfAny() {
            if (this.searching || this.searchResults.length > 0) {
                this.isListVisible = true;
            }
        },
        handleAccountSelection(account) {
            this.debouncedFetchData.cancel();
            this.abortController?.abort();
            this.setAccount(account);
            this.selectName();
            this.isListVisible = false;
            this.isSelecting = false;
            this.$router.push({ name: 'accounts-info', query: { id: account.id } });
        },
        handleSearchCancel() {
            this.setAccount(0);
            this.searchTerm = '';
            this.resetSearchData();
        },
        selectName(){
            this.searchTerm = this.account.first_name ? `${this.account.first_name} ${this.account.last_name || ''}` : `${this.account.email}`;
        }
    },
    watch: {
        searchTerm(new_value) {
            const trimmed_value = new_value?.trim();
            
            if (!trimmed_value) {
                this.setAccount(0);
                this.resetSearchData();
                return;
            }

            if (this.isSelecting) {
                this.abortController?.abort();
                this.searching = true;
                this.isListVisible = true;
                this.debouncedFetchData();
            } else {
                this.isSelecting = true;
            }
        },
        account(newAccount) {
            if (!newAccount || newAccount === 0) {
                this.searchTerm = '';
                this.isSelecting = false;
            } else {
                this.isListVisible = false;
                this.searchResults = [];
                this.searching = false;
            }
        },
    },
    created() {
        this.debouncedFetchData = debounce(function () {
            this.fetchData();
        }, 500);
    },
    mounted() {
        if (this.account && this.account !== 0) {
            this.isSelecting = false;
            this.selectName();
        }
    },
    beforeUnmount() {
        this.debouncedFetchData.cancel();
        this.abortController?.abort();
    },
};
</script>

<style scoped>
.search-container {
    position: relative;
    width: 400px;
}

.results-list {
    list-style-type: none;
    padding: 8px 0;
    margin: 4px 0 0 0;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    max-height: 300px;
    overflow-y: auto;
    position: absolute;
    width: 100%;
    background-color: white;
    z-index: 1000;
}

.result-item {
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.2s ease;
}

.result-item:last-child {
    border-bottom: none;
}

.result-button {
    display: flex;
    align-items: center;
    width: 100%;
    border: none;
    background-color: transparent;
    text-align: left;
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.result-button:hover {
    background-color: #f5f5f5;
}

.result-button:active,
.result-item:hover .result-button {
    background-color: #e8f5e9;
}

.result-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 12px;
}

.result-main {
    display: flex;
    flex-direction: column;
    flex: 1 1 58%;
    min-width: 0;
}

.result-name {
    font-weight: 600;
    font-size: 13px;
    color: #212121;
    line-height: 1.4;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.result-email {
    font-size: 12px;
    color: #757575;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.result-badge {
    flex: 0 1 42%;
    max-width: 42%;
    min-width: 0;
    padding: 4px 8px;
    background-color: #f5f5f5;
    border-radius: 4px;
    margin-left: auto;
}

.result-badge-text {
    display: block;
    font-size: 11px;
    font-weight: 500;
    color: #424242;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.skeleton-line {
    display: block;
    height: 12px;
    border-radius: 4px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: search-skeleton 1.2s infinite linear;
}

.skeleton-line--name {
    width: 45%;
    margin-bottom: 8px;
}

.skeleton-line--email {
    width: 70%;
    height: 10px;
}

@keyframes search-skeleton {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
}

.result-item:hover .result-badge {
    background-color: #e0e0e0;
}

.results-list::-webkit-scrollbar {
    width: 6px;
}

.results-list::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.results-list::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
}

.results-list::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
</style>
